import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {
  Box,
  DialogContent,
  DialogTitle,
  Typography,
  Dialog,
  Theme,
  Button,
  LinearProgress,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { atom, useStateXAtom } from '@cloudio-saas/statex';
import { Icon, Platform, useUser } from '../cloudio';
import { makeStyles } from '@mui/styles';
import { FileRejection, useDropzone } from 'react-dropzone';
import { camelCase, upperFirst } from 'lodash-es';
import WaveDots from '../utils/WaveDots';
import clsx from 'clsx';
import axios from 'axios';
import { getUrl } from '../config/config';
import { getURLQueryStringToPost } from '../utils/url';
import { Result } from '../components/AttachmentField/Attachment';
import { isNotEmpty } from '../wf/layout/property-editors/isEmpty';
import { Actions, AnyColumn } from 'componentregistry';
import { processChildActions } from 'triggers/Triggers';
import { FormControl } from '@mui/material';
import { FormLabel } from '@mui/material';
import { RadioGroup } from '@mui/material';
import { isEmpty } from 'utils/isEmpty';
import { getColumn } from 'actions/ActionExportData/ActionExportData';

interface ImportProps {
  datasourceUid?: string;
  dsAppUid?: string;
  fileType?: string;
  resolve?: (value: boolean) => void;
  itemId?: string;
  hideInsertMode?: boolean;
  hideUpdateMode?: boolean;
  mergeMode?: boolean;
  gridItemId?: string;
}

export const importPropsAtom = atom<ImportProps>({
  path: ['transient', 'import', ':appUid'],
  defaultValue: {},
});

interface Props {
  platform: Platform;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    gap: '16px',
  },
  dropzone: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: theme.palette.divider,
    borderStyle: 'dashed',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '&$dropped': {
      borderColor: theme.palette.success.light,
    },
    '&$dragActive': {
      borderColor: theme.palette.success.main,
    },
  },
  dragActive: {},
  dropped: {},
  file: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: theme.palette.divider,
    borderStyle: 'dashed',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function ImportData(props: Props) {
  const { platform } = props;
  const { appUid, pageId } = platform;
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const user = useUser();

  const [importProps, setImportProps] = useStateXAtom(importPropsAtom, {
    params: { appUid },
  });
  const {
    datasourceUid,
    dsAppUid,
    fileType,
    gridItemId,
    hideInsertMode,
    hideUpdateMode,
    mergeMode,
    itemId,
    resolve,
  } = importProps;
  const open = !!datasourceUid && !!fileType;
  const classes = useStyles();
  const [uploadMode, setUploadMode] = useState<string>('I');

  const onClose = (success: boolean, err: string) => {
    if (success) setImportProps({ mergeMode });
    setError(err);
    setFile(null);
    resolve?.(success);
  };

  const processChildren = async (
    count: number,
    fileName: string,
    id: string,
  ) => {
    const actionOnResponseChildren = platform.getChildrenItemsById(
      appUid,
      pageId,
      id,
      (child): boolean => child.type === 'ActionOnResponse',
    ) as Actions.ActionOnResponse.ActionOnResponseItem[];
    const all = [];
    for (let j = 0; j < actionOnResponseChildren.length; j++) {
      const { itemId: childItemId } = actionOnResponseChildren[j];
      all.push(
        processChildActions({
          ...{ appUid, platform, itemId, pageId },
          // @ts-ignore response
          response: {
            count,
            fileName,
            uploadMode,
          },
          itemId: childItemId,
        }),
      );
    }
    await Promise.all(all);
  };

  /* const onDownloadTemplate = async () => {
    if (!datasourceUid) return;
    const data = {
      ds: datasourceUid,
    };
    const url = `${getUrl()}download_template?${getURLQueryStringToPost(
      user,
    )}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/csv',
        Authorization: user.jwt,
        'X-Application': appUid,
      },
      body: JSON.stringify(data),
    });
    if (response.status === 200) {
      const blob = await response.blob();
      FileSaver.saveAs(blob, `${datasourceUid}.zip`);
    } else {
      const json = await response.json();
      platform.showError(`Template Download Error: ${json.message}`);
    }
  }; */

  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      if (fileRejections.length) {
        setFile(null);
        if (fileRejections.length > 1 || acceptedFiles.length) {
          setError('You must upload only a single file!');
        } else {
          const f = fileRejections[0];
          setError(
            `${f.file.name} is rejected! ${f.errors
              .map((e) => e.message)
              .join(', ')}!`,
          );
        }
      } else if (acceptedFiles.length === 1) {
        const f = acceptedFiles[0];
        if (f.size > 200000000) {
          setFile(null);
          setError(`File "${f.name}" is more than 200 MB!`);
        } else {
          let fileName = upperFirst(camelCase(f.name));
          if (fileType === 'csv' && !fileName.endsWith('CsvZip')) {
            setFile(null);
            setError('File name must end with .csv.zip!');
            return;
          }
          if (fileType === 'csv' && f.type !== 'application/zip') {
            setFile(null);
            setError('File must be in zip format');
            return;
          }
          if (
            fileType === 'xlsx' &&
            f.type !==
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          ) {
            setFile(null);
            setError('File must be in xlsx format');
            return;
          }
          if (fileName.endsWith('CsvZip')) {
            fileName = fileName.substring(0, fileName.length - 6);
          } else if (fileName.endsWith('XlsxZip')) {
            fileName = fileName.substring(0, fileName.length - 7);
          }
          setFile(f);
          setProgress(0);
          setError('');
          if (fileName.length > 40) {
            fileName = fileName.substring(0, 40);
          }
        }
      }
    },
    [fileType],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
    multiple: false,
  });

  useLayoutEffect(() => {
    let mode = 'I';
    if (hideInsertMode) {
      mode = 'U';
    }
    if (mergeMode) {
      mode = 'M';
    }
    setUploadMode(mode);
  }, [hideInsertMode, setUploadMode, mergeMode]);

  useEffect(() => {
    if (isNotEmpty(error)) {
      platform.showError(error);
    }
  }, [error, platform]);

  const content = (
    <div className={classes.root}>
      {!mergeMode && (
        <FormControl>
          <FormLabel id="demo-row-radio-buttons-group-label">
            Upload Mode
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            onChange={(e, value) => setUploadMode(value)}
            value={uploadMode}
          >
            {!hideInsertMode ? (
              <FormControlLabel
                value="I"
                control={
                  <Radio
                    disabled={
                      (progress > 0 && progress < 100) || hideUpdateMode
                    }
                  />
                }
                label="Insert"
              />
            ) : null}
            {!hideUpdateMode ? (
              <FormControlLabel
                value="U"
                control={
                  <Radio
                    disabled={
                      (progress > 0 && progress < 100) || hideInsertMode
                    }
                  />
                }
                label="Update"
              />
            ) : null}
          </RadioGroup>
        </FormControl>
      )}
      {!file ? (
        <div
          {...getRootProps()}
          className={clsx(classes.dropzone, {
            [classes.dragActive]: isDragActive,
            [classes.dropped]: !!file,
          })}
        >
          <input {...getInputProps()} />
          <Typography
            overflow="hidden"
            textOverflow="ellipsis"
            fontWeight={400}
            color={isNotEmpty(error) ? 'error' : 'default'}
          >
            {isNotEmpty(error)
              ? 'Error while processing Import. Check log for more info!'
              : `Drag 'n' drop a ${
                  fileType === 'csv' ? 'csv(zipped as .csv.zip)' : 'xlsx'
                } file here,
              or click here to select one.`}
          </Typography>
        </div>
      ) : progress > 0 && progress < 100 ? (
        <>
          <Typography variant="subtitle1" fontWeight={500}>
            Processing...
          </Typography>
          <LinearProgress color="success" />
        </>
      ) : (
        <Box className={classes.file}>
          <Typography>{`File: ${file.name} of size ${
            Math.round((file.size / 1024) * 100) / 100
          } Kilobytes is ready to be uploaded`}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="medium"
          fullWidth={false}
          variant="contained"
          disabled={!file || !!progress}
          sx={{ mr: '8px' }}
          startIcon={<Icon iconName="times" parentColor="primary" />}
          onClick={() => {
            setFile(null);
          }}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          fullWidth={false}
          variant="contained"
          disabled={!file || !!error || !!progress}
          startIcon={
            progress > 0 && progress < 100 ? (
              <WaveDots active />
            ) : (
              <Icon iconName="cloud-upload" parentColor="primary" />
            )
          }
          onClick={() => {
            if (!file) {
              platform.showWarning('You must select the file first!');
              return;
            }
            if (!(datasourceUid && itemId)) return;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('datasourceUid', datasourceUid);
            formData.append('uploadMode', uploadMode);
            if (isNotEmpty(gridItemId)) {
              formData.append('columns', JSON.stringify(columnsJson));
            }
            setProgress(0.001);
            axios({
              url: `${getUrl()}import_data/${fileType}?${getURLQueryStringToPost(
                user,
              )}`,
              method: 'POST',
              withCredentials: true,
              headers: {
                'X-Application': dsAppUid || appUid,
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              data: formData,
              onUploadProgress: (progressEvent) => {
                setProgress(
                  (progressEvent.loaded / (progressEvent.total ?? 100)) * 90,
                );
              },
            })
              .then(async ({ data }) => {
                const _data = data as Result & { count: number };
                if (_data.status === 'ERROR') {
                  setProgress(0);
                  onClose(false, _data.message);
                } else {
                  setProgress(100);
                  await processChildren(_data.count, file.name, itemId);
                  onClose(true, '');
                }
              })
              .catch((e) => {
                setProgress(0);
                onClose(false, e.response?.data?.message ?? e.message);
              });
          }}
        >
          Upload
        </Button>
      </Box>
    </div>
  );

  const columnsJson: Record<string, unknown>[] = [];
  if (isNotEmpty(gridItemId)) {
    const gridColumns = (
      platform.getChildrenItemsById(
        appUid,
        pageId,
        gridItemId,
        (child): boolean => true,
      ) as AnyColumn[]
    ).filter((child) => {
      return (
        child.type.includes('Column') &&
        child.props.viewAttribute &&
        child.type !== 'DivColumn' &&
        (isEmpty(child.props.visibleCondition)
          ? child.props.visible
          : child.props.visible &&
            platform.evalBoolean(
              appUid,
              pageId,
              child.itemId,
              'visibleCondition',
              { ...child.props },
              child.props.visibleCondition,
            ))
      );
    });
    gridColumns.forEach((column) => {
      columnsJson.push(getColumn(column, platform, appUid, pageId));
    });
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            fontWeight={600}
            variant="h6"
          >{`Import Data - ${datasourceUid ?? ''}`}</Typography>
          <Box>
            {/* <Icon
              onClick={onDownloadTemplate}
              color="primary"
              iconName="download"
              sx={{ mr: '12px' }}
              data-tip="Download template"
            /> */}
            <Icon
              disabled={progress > 0 && progress < 100}
              onClick={() => onClose(true, '')}
              iconName="times"
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
    </Dialog>
  );
}
